import { supabase } from "@/integrations/supabase/client";

const OPEN_STATUSES = ["pending", "paused"];

interface ActivityLike {
  id: string;
  org_id: string;
  deal_id?: string | null;
  lead_id?: string | null;
  company_id?: string | null;
}

/**
 * True if there exists another activity (same org) linked to the same deal, lead, or company,
 * with status still open and scheduled after `now`, excluding `excludeActivityId`.
 */
export async function hasFutureOpenActivitiesForContext(params: {
  orgId: string;
  dealId?: string | null;
  leadId?: string | null;
  companyId?: string | null;
  /** When set, excludes that activity id from the count (e.g. the one just completed). */
  excludeActivityId?: string | null;
}): Promise<boolean> {
  const { orgId, dealId, leadId, companyId, excludeActivityId } = params;
  const clauses: string[] = [];
  if (dealId) clauses.push(`deal_id.eq.${dealId}`);
  if (leadId) clauses.push(`lead_id.eq.${leadId}`);
  if (companyId) clauses.push(`company_id.eq.${companyId}`);
  if (clauses.length === 0) return false;

  const nowIso = new Date().toISOString();

  let q = supabase
    .from("activities")
    .select("id", { count: "exact", head: true })
    .eq("org_id", orgId)
    .in("status", OPEN_STATUSES as never[])
    .gt("date", nowIso)
    .or(clauses.join(","));

  if (excludeActivityId) {
    q = q.neq("id", excludeActivityId);
  }

  const { count, error } = await q;

  if (error) {
    console.error("[hasFutureOpenActivitiesForContext]", error);
    return true;
  }
  return (count ?? 0) > 0;
}

/** Load deal.pipeline_id for scope checks. */
export async function getDealPipelineId(dealId: string | null | undefined): Promise<string | null> {
  if (!dealId) return null;
  const { data, error } = await supabase.from("deals").select("pipeline_id").eq("id", dealId).maybeSingle();
  if (error) {
    console.error("[getDealPipelineId]", error);
    return null;
  }
  return (data as { pipeline_id?: string } | null)?.pipeline_id ?? null;
}
